import * as React from "react";
import { useAppSelector } from "../../../store/hooks";
import { Button } from "antd";
import { formSet, formSetValue, formTriggerValidation } from "./utils";
import { cloneDeep, get } from "lodash";
import { CpErrorWarning } from "./CpErrorWarning";
import { FieldLabels, FormListFieldLabels } from "../formListFieldLabels";
import { CpLabel } from "./CpLabel";
import { CpDescription } from "./CpDescription";
import { CpFormItem } from "./CpFormItem";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpLabelTooltip } from "./CpLabelTooltip";
import { CpTooltip } from "./CpTooltip";
import { CpLabelDirtyIndicator } from "./CpLabelDirtyIndicator";
import { Only } from "../../workload/draft/only";

interface DirtyIndicatorConfig {
  isEnabled: boolean;
  only?: {
    name: string;
    checkFn: (value: any) => boolean;
  };
}

interface Props extends React.PropsWithChildren {
  label?: string;
  name: string;
  description?: string;
  labels?: FieldLabels;
  factoryFn: () => any;
  children: any;
  dirtyIndicator?: DirtyIndicatorConfig;
}
export const CpArrayObject: React.FC<Props> = ({
  label,
  name,
  description,
  labels,
  factoryFn,
  children,
  dirtyIndicator,
}) => {
  const { id } = useCpFormContext();
  const items: any[] = useAppSelector((state) => get(state, `form.instances.${id}.values.${name}`)) || [];

  const renderDirtyIndicator = () => {
    if (!dirtyIndicator?.isEnabled) return null;

    const indicator = <CpLabelDirtyIndicator name={name} />;

    if (dirtyIndicator.only) {
      return (
        <Only name={dirtyIndicator.only.name} checkFn={dirtyIndicator.only.checkFn}>
          {indicator}
        </Only>
      );
    }

    return indicator;
  };

  function add() {
    const _items = cloneDeep(items);
    const newIndex = _items.length;
    const newItem = factoryFn();
    _items.push(newItem);
    formSetValue(id, name, _items);

    for (const key of Object.keys(newItem)) {
      formSet(id, `fieldStates.${name}.${newIndex}.${key}.simulateTouched`, true);
    }

    formTriggerValidation(id);
  }

  return (
    <CpFormItem>
      {label && description ? (
        <CpLabelTooltip>
          <CpLabel name={`skip`}>
            {label}
            {renderDirtyIndicator()}
          </CpLabel>
          <CpTooltip tooltipProps={{ title: description }} />
        </CpLabelTooltip>
      ) : label ? (
        <CpLabel name={`skip`}>
          {label}
          {renderDirtyIndicator()}
        </CpLabel>
      ) : description ? (
        <CpDescription>{description}</CpDescription>
      ) : null}
      {items.length < 1 || !labels ? null : (
        <FormListFieldLabels className={`mb-1`} fieldLabels={labels} hasRemoveButton ng />
      )}
      {items.map((item, index) => {
        return React.Children.map(children, (child) =>
          React.cloneElement(child as any, {
            key: item.id,
            name: name,
            item: item,
            index: index,
          }),
        );
      })}
      <Button block type={`dashed`} onClick={() => add()} iconPosition="start" icon={<PlusCircleOutlined />}>
        Add
      </Button>
      <CpErrorWarning name={`${name}root`} />
    </CpFormItem>
  );
};
